/**
 * DualViewportEditor - Phases 3-5 of Character Builder
 *
 * Features:
 * - Phase 3: Dual viewport generation (body 9:16 + head 3:4)
 * - Phase 4: Transpose system (head onto body composition)
 * - Phase 5: NSFW variant generation (z-image-turbo only)
 *
 * Each viewport has independent editing capabilities.
 * Transpose must be complete before final confirmation.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CircleNotchIcon,
  ArrowLeftIcon,
  PencilSimpleIcon,
  ArrowsClockwiseIcon,
  EyeSlashIcon,
  EyeIcon,
  CaretRightIcon,
  UserIcon,
  SmileyIcon,
  SparkleIcon,
  WarningIcon,
  ArrowUUpLeftIcon,
  ArrowUUpRightIcon,
  ArrowsOutIcon,
  UsersIcon
} from '@phosphor-icons/react';
import { FullscreenImageViewer, ViewerImage } from '../FullscreenImageViewer';
import { createViewportSaveQueue } from '../../lib/viewportSaveQueue';
import { generateImage, transposeHeadOntoBody, editImageWithConversation, generateBodyWithHeadReference } from '../../lib/imageGeneration';
import { buildEnforcedGenerationPrompt, isPhotographicStyle } from '../../lib/layerPrompts';
import { isContentModerationError } from '../../lib/generationErrors';
import { CharacterStylePreset, CharacterArchetype, CharacterForm, ReferenceImage } from '../../types';
import { getArchetypeLayerPrompt } from '../../lib/characterArchetypes';
import { SplitPrompts } from '../../lib/geminiCharacterAnalysis';
import { StyleOption } from '../../lib/characterStyles';
import { uploadTempCharacterImage } from '../../lib/character2dService';
import { deleteFromR2 } from '../../lib/storage';
import { classifyReferenceImages, generateReferenceImageInstructions, ClassifiedReferenceImage } from '../../lib/geminiPromptEnhancer';

// Helper to convert blob to base64 (for CORS-safe transpose)
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data:image/xxx;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper to fetch base64 via proxy (for CORS-blocked R2 URLs)
const fetchBase64ViaProxy = async (imageUrl: string): Promise<string | null> => {
  // Only proxy R2 URLs
  if (!imageUrl.includes('r2.dev')) {
    console.log('⏭️ URL is not R2, skipping proxy:', imageUrl.substring(0, 50));
    return null;
  }

  try {
    console.log('📥 Fetching base64 via proxy for:', imageUrl.substring(0, 60) + '...');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-proxy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ imageUrl }),
      }
    );

    if (!response.ok) {
      console.error('❌ Proxy fetch failed:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    console.log('✅ Proxy fetched base64:', data.base64?.length, 'chars');
    return data.base64;
  } catch (error) {
    console.error('❌ Proxy fetch error:', error);
    return null;
  }
};

// ============================================
// TYPES
// ============================================

interface DualViewportEditorProps {
  bodyPrompt: string;
  headPrompt: string;
  sharedAttributes: SplitPrompts['sharedAttributes'];
  stylePreset: CharacterStylePreset;
  archetype?: CharacterArchetype;
  form: CharacterForm;
  generationModel: 'openai/gpt-image-2.5-sunburst' | 'prunaai/z-image-turbo';
  quality: 'HQ' | 'HD';
  userId: string;
  projectId: string;
  characterId: string;
  selectedStyle?: StyleOption;  // Style with hardcoded keywords for generation
  referenceImages?: ReferenceImage[];  // User-uploaded reference images for generation
  // Viewport state persistence (from Supabase builder_session.viewport_state)
  initialViewportState?: ViewportState;
  initialCustomBodyUrl?: string;
  onViewportStateChange?: (state: ViewportState) => void | Promise<void>;
  onConfirm: (result: DualViewportResult) => void;
  onBack: () => void;
  onReturnToCharacters?: () => void;  // Optional callback to return to character selection
}

export interface DualViewportResult {
  bodyImageUrl: string;
  customBodyImageUrl?: string;
  headImageUrl: string;
  transposedBodyUrl: string;
  nsfwImageUrl?: string;
  bodyPrompt: string;
  headPrompt: string;
  sharedAttributes: SplitPrompts['sharedAttributes'];
  // Blobs for direct upload (bypasses CORS issues when moving to final storage)
  bodyBlob?: Blob;
  headBlob?: Blob;
  transposedBodyBlob?: Blob;
  nsfwBlob?: Blob;
}

interface TransposeState {
  lastTransposeTimestamp: number | null;
  headLastModified: number;
  bodyLastModified: number;
  transposedBodyUrl: string | null;
  currentTransposeVersion: number;
  isTransposing: boolean;
}

interface NSFWState {
  nsfwImageUrl: string | null;
  nsfwTransposeVersion: number;
  isGenerating: boolean;
}

// Edit model type: 'default' = conversational edit, 'testing' = z-image-turbo
type EditModelType = 'default' | 'testing';

// Persistable history entry (subset of HistoryEntry without base64/blobs)
interface PersistableHistoryEntry {
  bodyImageUrl: string | null;
  customBodyImageUrl?: string | null;
  headImageUrl: string | null;
  transposeSnapshot: {
    lastTransposeTimestamp: number | null;
    headLastModified: number;
    bodyLastModified: number;
    transposedBodyUrl: string | null;
    currentTransposeVersion: number;
  };
  nsfwSnapshot: {
    nsfwImageUrl: string | null;
    nsfwTransposeVersion: number;
  };
  nsfwEnabled: boolean;
  timestamp: number;
}

// Persistable state interface (excludes loading flags and refs)
// This is saved to Supabase via character.builder_session.viewport_state
export interface ViewportState {
  bodyImageUrl: string | null;
  customBodyImageUrl?: string | null;
  headImageUrl: string | null;
  transposeState: {
    lastTransposeTimestamp: number | null;
    headLastModified: number;
    bodyLastModified: number;
    transposedBodyUrl: string | null;
    currentTransposeVersion: number;
  };
  nsfwState: {
    nsfwImageUrl: string | null;
    nsfwTransposeVersion: number;
  };
  nsfwEnabled: boolean;
  bodyViewTab: 'original' | 'custom' | 'composite' | 'nsfw';
  timestamp: number;
  // History for undo/redo (limited to last 20 entries to avoid excessive storage)
  history?: PersistableHistoryEntry[];
  historyIndex?: number;
}

// History entry captures the complete state at a point in time
// Composite and NSFW are tied to their source images
interface HistoryEntry {
  bodyImageUrl: string | null;
  customBodyImageUrl?: string | null;
  headImageUrl: string | null;
  // Transpose state without loading flag
  transposeSnapshot: {
    lastTransposeTimestamp: number | null;
    headLastModified: number;
    bodyLastModified: number;
    transposedBodyUrl: string | null;
    currentTransposeVersion: number;
  };
  // NSFW state without loading flag
  nsfwSnapshot: {
    nsfwImageUrl: string | null;
    nsfwTransposeVersion: number;
  };
  nsfwEnabled: boolean;
  timestamp: number;
}

// ============================================
// COMPONENT
// ============================================

export default function DualViewportEditor({
  bodyPrompt: initialBodyPrompt,
  headPrompt: initialHeadPrompt,
  sharedAttributes,
  stylePreset,
  archetype,
  form,
  generationModel,
  quality,
  userId,
  projectId,
  characterId,
  selectedStyle,
  referenceImages,
  initialViewportState,
  initialCustomBodyUrl,
  onViewportStateChange,
  onConfirm,
  onBack,
  onReturnToCharacters
}: DualViewportEditorProps) {
  // localStorage key for viewport images (unique per character)
  const VIEWPORT_STORAGE_KEY = `ezart_viewport_${characterId}`;

  // Back confirmation modal state
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  // Load from localStorage as fast cache (before Supabase responds)
  const getLocalStorageViewport = (): { bodyImageUrl?: string; customBodyImageUrl?: string; headImageUrl?: string; transposedBodyUrl?: string; nsfwImageUrl?: string } | null => {
    try {
      const stored = localStorage.getItem(VIEWPORT_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  // Use viewport state from Supabase (passed via props), fallback to localStorage for fast load
  const localCache = getLocalStorageViewport();
  const savedState = initialViewportState || null;
  // Has restored state if initial viewport state has images OR localStorage cache
  const hasRestoredState = !!savedState?.bodyImageUrl || !!savedState?.headImageUrl ||
    !!localCache?.bodyImageUrl || !!localCache?.headImageUrl;

  // Image state - restore from saved viewport state (Supabase), fallback to localStorage cache
  const [bodyImageUrl, setBodyImageUrl] = useState<string | null>(
    savedState?.bodyImageUrl || localCache?.bodyImageUrl || null
  );
  const [customBodyImageUrl, setCustomBodyImageUrl] = useState<string | null>(
    savedState && 'customBodyImageUrl' in savedState
      ? savedState.customBodyImageUrl || null
      : initialCustomBodyUrl || localCache?.customBodyImageUrl || null
  );
  const [headImageUrl, setHeadImageUrl] = useState<string | null>(
    savedState?.headImageUrl || localCache?.headImageUrl || null
  );
  // Base64 data for transpose (avoids CORS issues with R2 URLs)
  const [bodyImageBase64, setBodyImageBase64] = useState<string | null>(null);
  const [headImageBase64, setHeadImageBase64] = useState<string | null>(null);

  // Blob data for CORS-safe finalization (bypasses fetch from R2 URLs)
  const [bodyBlob, setBodyBlob] = useState<Blob | null>(null);
  const [headBlob, setHeadBlob] = useState<Blob | null>(null);
  const [transposedBodyBlob, setTransposedBodyBlob] = useState<Blob | null>(null);
  const [nsfwBlob, setNsfwBlob] = useState<Blob | null>(null);

  // Prompt state (for editing)
  const [bodyEditPrompt, setBodyEditPrompt] = useState('');
  const [headEditPrompt, setHeadEditPrompt] = useState('');

  // Track last used prompts for regeneration (initial or edit)
  const [lastBodyPrompt, setLastBodyPrompt] = useState(initialBodyPrompt);
  const [lastHeadPrompt, setLastHeadPrompt] = useState(initialHeadPrompt);
  // Track if last generation was an edit (for regenerate logic)
  const [lastBodyWasEdit, setLastBodyWasEdit] = useState(false);
  const [lastHeadWasEdit, setLastHeadWasEdit] = useState(false);

  // Edit mode
  const [isEditingBody, setIsEditingBody] = useState(false);
  const [isEditingHead, setIsEditingHead] = useState(false);

  // Loading states
  // Initialize isGeneratingHead to true if we'll be generating (not restored)
  // This shows "Generating head..." immediately instead of a delay
  const [isGeneratingBody, setIsGeneratingBody] = useState(false);
  const [isGeneratingHead, setIsGeneratingHead] = useState(!hasRestoredState);
  const [hasStartedGeneration, setHasStartedGeneration] = useState(hasRestoredState); // Skip generation if restored


  // Fade animation state
  // Initialize to true if we have images (restored), false otherwise (new gen)
  const [bodyImageLoaded, setBodyImageLoaded] = useState(false);
  const [headImageLoaded, setHeadImageLoaded] = useState(false);

  // Initialize loaded state based on restored images on mount
  useEffect(() => {
    if (bodyImageUrl) setBodyImageLoaded(true);
    if (headImageUrl) setHeadImageLoaded(true);
  }, []); // Only on mount logic, subsequent updates handled by generation/onLoad

  // Reset loaded state when starting generation
  useEffect(() => {
    if (isGeneratingBody) setBodyImageLoaded(false);
  }, [isGeneratingBody]);

  useEffect(() => {
    if (isGeneratingHead) setHeadImageLoaded(false);
  }, [isGeneratingHead]);



  // Strength sliders for editing
  const [bodyStrength, setBodyStrength] = useState(0.65);
  const [headStrength, setHeadStrength] = useState(0.65);

  // Edit model selection (Default = conversational edit, Testing = z-image-turbo)
  const [bodyEditModel, setBodyEditModel] = useState<'default' | 'testing'>('default');
  const [headEditModel, setHeadEditModel] = useState<'default' | 'testing'>('default');

  // Regeneration model selection (for Try Again button)
  // Default inherits from prop, Testing forces z-image-turbo
  const [regenModel, setRegenModel] = useState<'default' | 'testing'>(
    generationModel === 'prunaai/z-image-turbo' ? 'testing' : 'default'
  );
  // Ref to track the model override for Try Again (used by body generation useEffect)
  const regenModelOverrideRef = useRef<'openai/gpt-image-2.5-sunburst' | 'prunaai/z-image-turbo' | null>(null);

  // Reference image classification state
  // Initialize isClassifyingReferences to true if there are reference images,
  // so generation waits for classification to complete before starting
  const [classifiedReferenceImages, setClassifiedReferenceImages] = useState<ClassifiedReferenceImage[]>([]);
  const [isClassifyingReferences, setIsClassifyingReferences] = useState(
    () => !!(referenceImages && referenceImages.length > 0)
  );
  const [referenceInstructions, setReferenceInstructions] = useState<{
    headInstructions: string;
    bodyInstructions: string;
    generalInstructions: string;
  }>({ headInstructions: '', bodyInstructions: '', generalInstructions: '' });

  // Transpose state - restore from saved viewport state (from Supabase)
  const [transposeState, setTransposeState] = useState<TransposeState>(() => {
    if (savedState?.transposeState) {
      return { ...savedState.transposeState, isTransposing: false };
    }
    return {
      lastTransposeTimestamp: null,
      headLastModified: 0,
      bodyLastModified: 0,
      transposedBodyUrl: null,
      currentTransposeVersion: 0,
      isTransposing: false
    };
  });

  // NSFW state - restore from saved if available
  const [nsfwState, setNsfwState] = useState<NSFWState>(() => {
    if (savedState?.nsfwState) {
      return { ...savedState.nsfwState, isGenerating: false };
    }
    return {
      nsfwImageUrl: null,
      nsfwTransposeVersion: -1,
      isGenerating: false
    };
  });
  const [nsfwEnabled, setNsfwEnabled] = useState(savedState?.nsfwEnabled || false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Body view tab state: which image to show in body viewport
  type BodyViewTab = 'original' | 'custom' | 'composite' | 'nsfw';
  const [bodyViewTab, setBodyViewTab] = useState<BodyViewTab>(savedState?.bodyViewTab || 'original');

  // Track if user has made edits since initial generation
  // If no edits, user can confirm without transpose
  // If edits have been made, transpose is required
  // Derive initial value from saved transposeState timestamps (if user had edited before leaving)
  const [hasUserEditedHead, setHasUserEditedHead] = useState(() => {
    if (savedState?.transposeState?.headLastModified) {
      // User had edited head before - check if it was after the last transpose
      const headModified = savedState.transposeState.headLastModified;
      const lastTranspose = savedState.transposeState.lastTransposeTimestamp || 0;
      return headModified > lastTranspose;
    }
    return false;
  });
  const [hasUserEditedBody, setHasUserEditedBody] = useState(() => {
    if (savedState?.transposeState?.bodyLastModified) {
      // User had edited body before - check if it was after the last transpose
      const bodyModified = savedState.transposeState.bodyLastModified;
      const lastTranspose = savedState.transposeState.lastTransposeTimestamp || 0;
      return bodyModified > lastTranspose;
    }
    return false;
  });

  // Fullscreen viewer state
  const [fullscreenImage, setFullscreenImage] = useState<'body' | 'head' | null>(null);

  // Ref to track initial generation
  const hasGeneratedRef = useRef(hasRestoredState); // Already "generated" if restored

  // ============================================
  // HISTORY / UNDO-REDO STATE
  // ============================================
  // Restore history from savedState if available
  const [history, setHistory] = useState<HistoryEntry[]>(() => {
    if (savedState?.history && savedState.history.length > 0) {
      // Convert PersistableHistoryEntry to HistoryEntry (they're compatible)
      return savedState.history.map(entry => ({
        ...entry,
        // These fields match between the two interfaces
      }));
    }
    return [];
  });
  const [historyIndex, setHistoryIndex] = useState(() => {
    if (savedState?.historyIndex !== undefined && savedState.historyIndex >= 0) {
      return savedState.historyIndex;
    }
    return -1;
  });
  const isRestoringFromHistory = useRef(false);

  // Derived: can undo/redo
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Derived state - needs transpose only if user has edited head or body AFTER initial generation
  // After initial generation (no edits), user can confirm without transpose
  // If user edits head or body, they must transpose before confirming
  const hasUserEdited = hasUserEditedHead || hasUserEditedBody;
  const needsTranspose = hasUserEdited && (
    transposeState.headLastModified > (transposeState.lastTransposeTimestamp || 0) ||
    transposeState.bodyLastModified > (transposeState.lastTransposeTimestamp || 0)
  );
  // Can confirm if:
  // 1. No user edits made (initial generation complete) - can confirm with original body
  // 2. User edited AND transpose is complete (transposedBodyUrl exists and up-to-date)
  const canConfirm = !transposeState.isTransposing && bodyImageUrl && headImageUrl && (
    !hasUserEdited || // No edits - can confirm with original
    (hasUserEdited && transposeState.transposedBodyUrl && !needsTranspose) // Edited and transposed
  );
  const isAnyLoading = isGeneratingBody || isGeneratingHead || transposeState.isTransposing || nsfwState.isGenerating;

  // Get style suffix from archetype + selected style
  // IMPORTANT: selectedStyle.promptKeywords are HARDCODED into every generation
  const getStyleSuffix = useCallback((layerType: 'body' | 'head' = 'body') => {
    const parts: string[] = [];

    // Add archetype-specific layer prompt
    if (archetype) {
      const archetypePrompt = getArchetypeLayerPrompt(archetype, layerType);
      if (archetypePrompt) {
        parts.push(archetypePrompt);
      }
    } else {
      // Fallback style prompts based on stylePreset string
      const stylePrompts: Record<CharacterStylePreset, string> = {
        'photorealistic': 'photorealistic, 8K photo, cinematic film quality, realistic textures',
        'anime': 'anime style, cel shaded 2D anime illustration, Japanese animation character',
        'comic': 'comic book style, western graphic novel illustration, bold ink lines'
      };
      if (stylePrompts[stylePreset]) {
        parts.push(stylePrompts[stylePreset]);
      }
    }

    // HARDCODE selected style keywords into ALL generations
    if (selectedStyle?.promptKeywords && selectedStyle.promptKeywords.length > 0) {
      parts.push(selectedStyle.promptKeywords.join(', '));
    }

    return parts.join(', ');
  }, [archetype, stylePreset, selectedStyle]);

  // ============================================
  // HISTORY FUNCTIONS
  // ============================================

  // Save current state to history
  const saveToHistory = useCallback(() => {
    // Don't save while restoring from history
    if (isRestoringFromHistory.current) return;
    // Don't save if we don't have at least one image
    if (!bodyImageUrl && !headImageUrl) return;

    const newEntry: HistoryEntry = {
      bodyImageUrl,
      customBodyImageUrl,
      headImageUrl,
      transposeSnapshot: {
        lastTransposeTimestamp: transposeState.lastTransposeTimestamp,
        headLastModified: transposeState.headLastModified,
        bodyLastModified: transposeState.bodyLastModified,
        transposedBodyUrl: transposeState.transposedBodyUrl,
        currentTransposeVersion: transposeState.currentTransposeVersion,
      },
      nsfwSnapshot: {
        nsfwImageUrl: nsfwState.nsfwImageUrl,
        nsfwTransposeVersion: nsfwState.nsfwTransposeVersion,
      },
      nsfwEnabled,
      timestamp: Date.now(),
    };

    setHistory(prev => {
      // If we're not at the end, truncate forward history
      const truncated = prev.slice(0, historyIndex + 1);
      return [...truncated, newEntry];
    });
    setHistoryIndex(prev => prev + 1);
  }, [bodyImageUrl, customBodyImageUrl, headImageUrl, transposeState, nsfwState, nsfwEnabled, historyIndex]);

  // Restore state from a history entry
  const restoreFromHistory = useCallback((entry: HistoryEntry) => {
    isRestoringFromHistory.current = true;

    setBodyImageUrl(entry.bodyImageUrl);
    setCustomBodyImageUrl(entry.customBodyImageUrl || null);
    setHeadImageUrl(entry.headImageUrl);
    // Restored URLs must not be paired with blobs from a later history entry.
    setBodyBlob(null); setHeadBlob(null); setBodyImageBase64(null); setHeadImageBase64(null);
    setTransposedBodyBlob(null); setNsfwBlob(null);
    setTransposeState(prev => ({
      ...entry.transposeSnapshot,
      isTransposing: false, // Always reset loading state
    }));
    setNsfwState(prev => ({
      ...entry.nsfwSnapshot,
      isGenerating: false, // Always reset loading state
    }));
    setNsfwEnabled(entry.nsfwEnabled);

    // Update view tab based on what's available
    if (entry.customBodyImageUrl) {
      setBodyViewTab('custom');
    } else if (entry.nsfwSnapshot.nsfwImageUrl) {
      setBodyViewTab('nsfw');
    } else if (entry.transposeSnapshot.transposedBodyUrl) {
      setBodyViewTab('composite');
    } else {
      setBodyViewTab('original');
    }

    // Allow saving again after a short delay
    setTimeout(() => {
      isRestoringFromHistory.current = false;
    }, 100);
  }, []);

  // Undo handler
  const handleUndo = useCallback(() => {
    if (!canUndo || isAnyLoading) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    restoreFromHistory(history[newIndex]);
  }, [canUndo, isAnyLoading, historyIndex, history, restoreFromHistory]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (!canRedo || isAnyLoading) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    restoreFromHistory(history[newIndex]);
  }, [canRedo, isAnyLoading, historyIndex, history, restoreFromHistory]);

  // ============================================
  // GENERATION FUNCTIONS
  // ============================================

  const generateBodyImage = useCallback(async (
    prompt: string,
    isEdit: boolean = false,
    inputImage?: string,
    editModel: EditModelType = 'default',
    referenceHeadUrl?: string,  // For initial generation: use head as face reference
    referenceHeadBase64?: string,  // Base64 of head to avoid CORS when fetching from R2
    modelOverride?: 'openai/gpt-image-2.5-sunburst' | 'prunaai/z-image-turbo'
  ) => {
    setIsGeneratingBody(true);
    setError(null);

    // Use override if provided, otherwise use prop
    const effectiveModel = modelOverride || generationModel;

    try {
      const styleSuffix = getStyleSuffix();

      // Build reference image instructions prefix for body
      const refBodyPrefix = referenceInstructions.bodyInstructions
        ? `${referenceInstructions.bodyInstructions}\n\n`
        : referenceInstructions.generalInstructions
          ? `${referenceInstructions.generalInstructions}\n\n`
          : '';

      // Helper to build prompt with retry level for progressive content moderation handling
      // Retry levels: 0=normal bikini, 1=no "bare" words, 2=sports bra, 3=tube top
      const buildBodyPrompt = (retryLevel: number) => {
        const basePrompt = buildEnforcedGenerationPrompt(
          'body',
          prompt,
          styleSuffix,
          undefined,
          form,
          isEdit && !!inputImage,
          sharedAttributes.gender,
          false, // Not NSFW for body layer
          retryLevel
        );
        return refBodyPrefix + basePrompt;
      };

      let imageUrl: string | undefined;
      let imageBlob: Blob | undefined;

      // Helper function to attempt generation with given retry level
      const attemptGeneration = async (retryLevel: number): Promise<{ imageUrl?: string; imageBlob?: Blob }> => {
        const enforcedPrompt = buildBodyPrompt(retryLevel);

        const retryDescriptions = isPhotographicStyle(styleSuffix) ? ['athletic base layer', 'high-neck sports bra and short compression shorts'] : [
          'normal bikini',
          'without "bare" keywords',
          'sports bra + yoga shorts',
          'tube top + high-cut bottoms'
        ];
        if (retryLevel > 0) {
          console.log(`🔄 Retrying body generation (level ${retryLevel}: ${retryDescriptions[retryLevel]})...`);
        }

        if (isEdit && inputImage) {
          if (editModel === 'default') {
            // Default: Use reference image edit (Sunburst)
            // Pass base64 to avoid CORS issues when fetching R2 URLs
            const editResult = await editImageWithConversation({
              existingImageUrl: inputImage,
              existingImageBase64: bodyImageBase64 || undefined,
              editPrompt: `${prompt}. ${enforcedPrompt}`,
              aspectRatio: effectiveModel === 'openai/gpt-image-2.5-sunburst' ? '2:3' : '9:16'
            });
            const blob = editResult.imageBlob;
            // Conversational edit returns blob, upload to R2
            const filename = `body_${Date.now()}.png`;
            const url = await uploadTempCharacterImage(editResult.imageBlob, userId, projectId, characterId, filename);
            return { imageUrl: url, imageBlob: blob };
          } else {
            // Testing: Use z-image-turbo img2img with strength
            const result = await generateImage({
              prompt: enforcedPrompt,
              model: 'prunaai/z-image-turbo-img2img',
              aspectRatio: effectiveModel === 'openai/gpt-image-2.5-sunburst' ? '2:3' : '9:16',
              inputImage,
              strength: bodyStrength,
              quality
            });
            let url = result.imageUrl;
            if (!url && result.imageBlob) {
              const filename = `body_${Date.now()}.png`;
              url = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
            }
            return { imageUrl: url, imageBlob: result.imageBlob };
          }
        } else if (referenceHeadUrl) {
          // Initial generation WITH head reference
          // If using Testing model (z-image-turbo), use local ComfyUI directly
          // Otherwise use Sunburst multi-image for face consistency
          if (effectiveModel === 'prunaai/z-image-turbo') {
            console.log('🎨 Generating body with Testing model (local ComfyUI)...');
            const result = await generateImage({
              prompt: enforcedPrompt,
              model: effectiveModel,
              aspectRatio: effectiveModel === 'openai/gpt-image-2.5-sunburst' ? '2:3' : '9:16',
              quality
            });
            let url = result.imageUrl;
            if (!url && result.imageBlob) {
              const filename = `body_${Date.now()}.png`;
              url = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
            }
            return { imageUrl: url, imageBlob: result.imageBlob };
          } else {
            // Use Sunburst multi-image for face consistency
            console.log('🎨 Generating body with head reference (Sunburst multi-image)...');

            const result = await generateBodyWithHeadReference({
              bodyPrompt: enforcedPrompt,
              headImageUrl: referenceHeadUrl,
              headImageBase64: referenceHeadBase64, // Pass base64 to bypass CORS fetch from R2
              aspectRatio: effectiveModel === 'openai/gpt-image-2.5-sunburst' ? '2:3' : '9:16',
              sharedAttributes
            });

            let url = result.imageUrl;
            if (!url && result.imageBlob) {
              const filename = `body_${Date.now()}.png`;
              url = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
            }
            return { imageUrl: url, imageBlob: result.imageBlob };
          }
        } else {
          // Initial generation without reference (fallback)
          const result = await generateImage({
            prompt: enforcedPrompt,
            model: effectiveModel,
            aspectRatio: effectiveModel === 'openai/gpt-image-2.5-sunburst' ? '2:3' : '9:16',
            quality
          });
          let url = result.imageUrl;
          if (!url && result.imageBlob) {
            const filename = `body_${Date.now()}.png`;
            url = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
          }
          return { imageUrl: url, imageBlob: result.imageBlob };
        }
      };

      // Progressive retry logic for content moderation
      // Level 0: normal bikini, Level 1: no "bare" words, Level 2: sports bra, Level 3: tube top
      const maxRetryLevel = isPhotographicStyle(styleSuffix) ? (sharedAttributes.gender === 'male' ? 0 : 1) : sharedAttributes.gender === 'female' ? 3 : 0;
      let lastError: Error | null = null;

      for (let retryLevel = 0; retryLevel <= maxRetryLevel; retryLevel++) {
        try {
          const result = await attemptGeneration(retryLevel);
          imageUrl = result.imageUrl;
          imageBlob = result.imageBlob;
          break; // Success, exit retry loop
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          const isContentModeration = isContentModerationError(lastError);

          if (isContentModeration && retryLevel < maxRetryLevel) {
            console.log(`⚠️ Content moderation triggered at level ${retryLevel}, trying level ${retryLevel + 1}...`);
            continue; // Try next level
          } else {
            throw lastError; // Re-throw if not content moderation or exhausted retries
          }
        }
      }

      if (imageUrl) {
        setBodyImageUrl(imageUrl);
        setCustomBodyImageUrl(null);
        setBodyViewTab('original');
        // Capture base64 for CORS-safe transpose
        if (imageBlob) {
          const base64 = await blobToBase64(imageBlob);
          setBodyImageBase64(base64);
          // Store blob for CORS-safe finalization
          setBodyBlob(imageBlob);
        }
        // When body changes, mark as modified and reset transpose state
        const now = Date.now();
        setTransposeState(prev => ({
          ...prev,
          bodyLastModified: now,
          // For initial generation (not edit), sync lastTransposeTimestamp so we don't require transpose
          lastTransposeTimestamp: isEdit ? prev.lastTransposeTimestamp : now,
          transposedBodyUrl: null,
          currentTransposeVersion: prev.currentTransposeVersion + 1
        }));
        // Reset transposed blob since body changed
        setTransposedBodyBlob(null);
        // Note: History save happens in effect when state settles
      }
    } catch (err) {
      console.error('Body generation failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate body image');
    } finally {
      setIsGeneratingBody(false);
    }
  }, [generationModel, quality, form, sharedAttributes, bodyStrength, getStyleSuffix, userId, projectId, characterId, referenceInstructions]);

  const generateHeadImage = useCallback(async (
    prompt: string,
    isEdit: boolean = false,
    inputImage?: string,
    editModel: EditModelType = 'default',
    modelOverride?: 'openai/gpt-image-2.5-sunburst' | 'prunaai/z-image-turbo'
  ) => {
    setIsGeneratingHead(true);
    setError(null);

    // Use override if provided, otherwise use prop
    const effectiveModel = modelOverride || generationModel;

    try {
      const styleSuffix = getStyleSuffix();

      // Build reference image instructions prefix for head
      const refHeadPrefix = referenceInstructions.headInstructions
        ? `${referenceInstructions.headInstructions}\n\n`
        : referenceInstructions.generalInstructions
          ? `${referenceInstructions.generalInstructions}\n\n`
          : '';

      const basePrompt = buildEnforcedGenerationPrompt(
        'head',
        prompt,
        styleSuffix,
        undefined,
        form,
        isEdit && !!inputImage,
        sharedAttributes.gender
      );
      const enforcedPrompt = refHeadPrefix + basePrompt;

      let imageUrl: string | undefined;
      let imageBlob: Blob | undefined;

      if (isEdit && inputImage) {
        if (editModel === 'default') {
          // Default: Use reference image edit (Sunburst)
          // Pass base64 to avoid CORS issues when fetching R2 URLs
          const editResult = await editImageWithConversation({
            existingImageUrl: inputImage,
            existingImageBase64: headImageBase64 || undefined,
            editPrompt: `${prompt}. ${enforcedPrompt}`,
            aspectRatio: '3:4'
          });
          imageBlob = editResult.imageBlob;
          // Conversational edit returns blob, upload to R2
          const filename = `head_${Date.now()}.png`;
          imageUrl = await uploadTempCharacterImage(editResult.imageBlob, userId, projectId, characterId, filename);
        } else {
          // Testing: Use z-image-turbo img2img with strength
          const result = await generateImage({
            prompt: enforcedPrompt,
            model: 'prunaai/z-image-turbo-img2img',
            aspectRatio: '3:4',
            inputImage,
            strength: headStrength,
            quality
          });
          imageUrl = result.imageUrl;
          imageBlob = result.imageBlob;
          if (!imageUrl && result.imageBlob) {
            const filename = `head_${Date.now()}.png`;
            imageUrl = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
          }
        }
      } else {
        // Initial generation - check for user-uploaded reference images
        // If user uploaded head or full-body reference images, use them as character reference
        const headReferences = classifiedReferenceImages.filter(
          img => img.classification === 'head' || img.classification === 'both'
        );

        if (headReferences.length > 0) {
          // Use reference images with Sunburst for character consistency
          console.log(`🎯 Using ${headReferences.length} reference image(s) for head generation`);
          const snapshotUrls = headReferences.map(img =>
            img.base64.startsWith('data:') ? img.base64 : `data:image/png;base64,${img.base64}`
          );

          const result = await generateImage({
            prompt: enforcedPrompt,
            model: effectiveModel,
            aspectRatio: '3:4',
            quality,
            characterReference: {
              name: 'User Reference',
              snapshotUrl: snapshotUrls[0],
              snapshotUrls: snapshotUrls,
              metadata: { source: 'user_upload' }
            }
          });
          imageUrl = result.imageUrl;
          imageBlob = result.imageBlob;
          if (!imageUrl && result.imageBlob) {
            const filename = `head_${Date.now()}.png`;
            imageUrl = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
          }
        } else {
          // No reference images - standard generation
          const result = await generateImage({
            prompt: enforcedPrompt,
            model: effectiveModel,
            aspectRatio: '3:4',
            quality
          });
          imageUrl = result.imageUrl;
          imageBlob = result.imageBlob;
          if (!imageUrl && result.imageBlob) {
            const filename = `head_${Date.now()}.png`;
            imageUrl = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
          }
        }
      }

      if (imageUrl) {
        // Capture base64 for CORS-safe transpose
        if (imageBlob) {
          const base64 = await blobToBase64(imageBlob);
          setHeadImageBase64(base64);
          // Store blob for CORS-safe finalization
          setHeadBlob(imageBlob);
        }
        // Publish the URL after its bytes are ready so auto body generation can reuse them.
        setHeadImageUrl(imageUrl);
        // Mark head as modified
        const now = Date.now();
        setTransposeState(prev => ({
          ...prev,
          headLastModified: now,
          // For initial generation (not edit), sync lastTransposeTimestamp so we don't require transpose
          lastTransposeTimestamp: isEdit ? prev.lastTransposeTimestamp : now
        }));
      }
    } catch (err) {
      console.error('Head generation failed:', err);
      setError('Failed to generate head image');
    } finally {
      setIsGeneratingHead(false);
    }
  }, [generationModel, quality, form, sharedAttributes, headStrength, getStyleSuffix, userId, projectId, characterId, referenceInstructions, classifiedReferenceImages]);

  // ============================================
  // FETCH BASE64 FOR R2 URLS (CORS WORKAROUND)
  // ============================================
  // When resuming a session, we may have R2 URLs but no base64 data.
  // This causes transpose to fail due to CORS. Fetch base64 via proxy.
  useEffect(() => {
    const fetchMissingBase64 = async () => {
      // Only run if we have URLs but missing base64
      if (bodyImageUrl && !bodyImageBase64 && bodyImageUrl.includes('r2.dev')) {
        console.log('🔄 Fetching missing body base64 via proxy...');
        const base64 = await fetchBase64ViaProxy(bodyImageUrl);
        if (base64) {
          setBodyImageBase64(base64);
        }
      }

      if (headImageUrl && !headImageBase64 && headImageUrl.includes('r2.dev')) {
        console.log('🔄 Fetching missing head base64 via proxy...');
        const base64 = await fetchBase64ViaProxy(headImageUrl);
        if (base64) {
          setHeadImageBase64(base64);
        }
      }
    };

    fetchMissingBase64();
  }, [bodyImageUrl, headImageUrl, bodyImageBase64, headImageBase64]);

  // Classify reference images on mount (before generation starts)
  useEffect(() => {
    const classifyImages = async () => {
      if (!referenceImages || referenceImages.length === 0) {
        // No reference images - ensure we're not blocking generation
        setIsClassifyingReferences(false);
        return;
      }

      setIsClassifyingReferences(true);
      console.log(`🔍 Classifying ${referenceImages.length} reference image(s)...`);

      try {
        // Prepare images for classification
        const imagesToClassify = referenceImages
          .filter(img => img.base64)
          .map(img => ({ id: img.id, base64: img.base64! }));

        if (imagesToClassify.length > 0) {
          const classified = await classifyReferenceImages(imagesToClassify);
          setClassifiedReferenceImages(classified);

          // Generate instructions based on classification
          const instructions = generateReferenceImageInstructions(
            classified,
            archetype,
            selectedStyle?.name
          );
          setReferenceInstructions(instructions);

          console.log('✅ Reference images classified:', classified.map(c => `${c.id}: ${c.classification}`));
          console.log('📝 Generated instructions:', instructions);
        } else {
          console.warn('⚠️ Reference images have no base64 data for classification');
        }
      } catch (error) {
        console.error('❌ Failed to classify reference images:', error);
      } finally {
        setIsClassifyingReferences(false);
      }
    };

    classifyImages();
  }, [referenceImages, archetype, selectedStyle?.name]);

  // Initial generation on mount - SEQUENTIAL: head first, then body with head as reference
  // This ensures face/skin tone consistency between head and body
  // IMPORTANT: Wait for reference image classification to complete first
  useEffect(() => {
    // Don't start until reference image classification is complete
    // (either no references, or classification finished)
    const hasReferences = referenceImages && referenceImages.length > 0;
    const classificationDone = !hasReferences || !isClassifyingReferences;

    if (!hasGeneratedRef.current && !hasStartedGeneration && classificationDone) {
      hasGeneratedRef.current = true;
      setHasStartedGeneration(true);

      // Sequential generation: head first, then body using head as face reference
      const generateSequentially = async () => {
        try {
          // Step 1: Generate head first
          console.log('🎨 Step 1: Generating head portrait first...');
          if (hasReferences && classifiedReferenceImages.length > 0) {
            console.log(`📸 Using ${classifiedReferenceImages.length} classified reference(s) for generation`);
          }
          await generateHeadImage(initialHeadPrompt);
        } catch (err) {
          console.error('Head generation failed:', err);
        }
        // Body generation is triggered by the effect below when headImageUrl changes
      };

      generateSequentially();
    }
  }, [initialHeadPrompt, generateHeadImage, hasStartedGeneration, isClassifyingReferences, referenceImages, classifiedReferenceImages]);

  // Step 2: When head completes, generate body using head as reference
  // This ensures the body's face matches the head perfectly
  const hasTriggeredBodyGenRef = useRef(false);
  useEffect(() => {
    // Only trigger once when head is generated for the first time
    if (headImageUrl && !bodyImageUrl && !isGeneratingBody && !hasTriggeredBodyGenRef.current && hasStartedGeneration) {
      hasTriggeredBodyGenRef.current = true;
      console.log('🎨 Step 2: Generating body with head reference for face consistency...');
      // Pass headImageBase64 to bypass CORS when fetching head from R2
      // Pass model override if set (for Try Again with different model)
      const modelOverride = regenModelOverrideRef.current || undefined;
      generateBodyImage(initialBodyPrompt, false, undefined, 'default', headImageUrl, headImageBase64 || undefined, modelOverride);
    }
  }, [headImageUrl, headImageBase64, bodyImageUrl, isGeneratingBody, initialBodyPrompt, generateBodyImage, hasStartedGeneration]);

  // ============================================
  // TRANSPOSE FUNCTION
  // ============================================

  const handleTranspose = useCallback(async () => {
    if (!bodyImageUrl || !headImageUrl || transposeState.isTransposing) return;

    setTransposeState(prev => ({ ...prev, isTransposing: true }));
    setError(null);

    try {
      const result = await transposeHeadOntoBody({
        bodyImageUrl,
        headImageUrl,
        // Pass base64 data to avoid CORS issues with R2 URLs
        bodyImageBase64: bodyImageBase64 || undefined,
        headImageBase64: headImageBase64 || undefined,
        characterDescription: `${sharedAttributes.gender} character with ${sharedAttributes.skinTone} skin`,
        form,
        quality,
        userId,
        projectId,
        characterId
      });

      setTransposeState(prev => ({
        ...prev,
        lastTransposeTimestamp: Date.now(),
        headLastModified: transposeState.headLastModified, // Mark as up to date with current head
        bodyLastModified: transposeState.bodyLastModified, // Mark as up to date with current body
        transposedBodyUrl: result.imageUrl,
        currentTransposeVersion: prev.currentTransposeVersion + 1,
        isTransposing: false
      }));

      // Store transposed blob for CORS-safe finalization
      if (result.imageBlob) {
        setTransposedBodyBlob(result.imageBlob);
      }
    } catch (err) {
      console.error('Transpose failed:', err);
      setError('Failed to composite head onto body');
      setTransposeState(prev => ({ ...prev, isTransposing: false }));
    }
  }, [bodyImageUrl, headImageUrl, bodyImageBase64, headImageBase64, sharedAttributes, transposeState.isTransposing, form, quality, userId, projectId, characterId, initialBodyPrompt]);

  // ============================================
  // NSFW FUNCTION
  // ============================================

  const generateNSFWVariant = useCallback(async () => {
    if (!transposeState.transposedBodyUrl || needsTranspose) return;

    setNsfwState(prev => ({ ...prev, isGenerating: true }));
    setError(null);

    try {
      // Always use z-image-turbo for NSFW (per spec)
      // Include style keywords in NSFW generation too
      const styleSuffix = getStyleSuffix('body');
      const nsfwPrompt = `${initialBodyPrompt}, ${styleSuffix}, REMOVE CLOTHING ONLY. completely nude, fully naked, remove all clothing, bare skin visible, no underwear, no bra, no clothes at all, natural nude body. IMPORTANT: Keep exact same face, hair, pose, body proportions, art style, and background. Only remove the clothing.`;

      const result = await generateImage({
        prompt: nsfwPrompt,
        model: 'prunaai/z-image-turbo-img2img',
        aspectRatio: '9:16',
        inputImage: transposeState.transposedBodyUrl,
        strength: 0.75, // Higher strength to remove clothing
        quality: 'HQ'
      });

      // Handle both imageUrl and imageBlob - upload blobs to R2 for persistence
      let imageUrl = result.imageUrl;
      if (!imageUrl && result.imageBlob) {
        // Upload blob to R2 instead of creating temporary blob URL
        const filename = `nsfw_${Date.now()}.png`;
        imageUrl = await uploadTempCharacterImage(result.imageBlob, userId, projectId, characterId, filename);
      }

      if (imageUrl) {
        setNsfwState({
          nsfwImageUrl: imageUrl,
          nsfwTransposeVersion: transposeState.currentTransposeVersion,
          isGenerating: false
        });
      } else {
        throw new Error('No image returned from NSFW generation');
      }
    } catch (err) {
      console.error('NSFW generation failed:', err);
      setError('Failed to generate NSFW variant');
      setNsfwState(prev => ({ ...prev, isGenerating: false }));
    }
  }, [transposeState.transposedBodyUrl, transposeState.currentTransposeVersion, needsTranspose, initialBodyPrompt, getStyleSuffix, userId, projectId, characterId]);

  // Generate NSFW when toggle is enabled and conditions are met
  useEffect(() => {
    // Don't auto-generate during history restore (undo/redo)
    if (isRestoringFromHistory.current) return;

    if (nsfwEnabled && canConfirm && !nsfwState.nsfwImageUrl && !nsfwState.isGenerating) {
      // Only generate if we don't have one for current transpose version
      if (nsfwState.nsfwTransposeVersion !== transposeState.currentTransposeVersion) {
        generateNSFWVariant();
      }
    }
  }, [nsfwEnabled, canConfirm, nsfwState, transposeState.currentTransposeVersion, generateNSFWVariant]);

  // Auto-switch to composite tab when transpose completes
  useEffect(() => {
    if (transposeState.transposedBodyUrl && !transposeState.isTransposing) {
      setBodyViewTab('composite');
    }
  }, [transposeState.transposedBodyUrl, transposeState.isTransposing]);

  // Auto-switch to NSFW tab when NSFW generation completes
  useEffect(() => {
    if (nsfwState.nsfwImageUrl && !nsfwState.isGenerating && nsfwEnabled) {
      setBodyViewTab('nsfw');
    }
  }, [nsfwState.nsfwImageUrl, nsfwState.isGenerating, nsfwEnabled]);

  // ============================================
  // HISTORY SAVE EFFECTS
  // ============================================

  // Save to history when body or head generation completes
  // Use a ref to track last saved state to avoid duplicate entries
  // Initialize with saved state signature to prevent counting page load as a change
  const initialStateSignature = (savedState?.bodyImageUrl || savedState?.headImageUrl)
    ? JSON.stringify({
      body: savedState?.bodyImageUrl || null,
      custom: savedState?.customBodyImageUrl || null,
      head: savedState?.headImageUrl || null,
      transpose: savedState?.transposeState?.transposedBodyUrl || null,
      nsfw: savedState?.nsfwState?.nsfwImageUrl || null,
    })
    : '';
  const lastSavedStateRef = useRef<string>(initialStateSignature);

  useEffect(() => {
    // Don't save while any generation is in progress
    if (isGeneratingBody || isGeneratingHead || transposeState.isTransposing || nsfwState.isGenerating) return;
    // Don't save while restoring
    if (isRestoringFromHistory.current) return;
    // Need at least one image to save
    if (!bodyImageUrl && !headImageUrl) return;

    // Create a signature to detect actual state changes
    const stateSignature = JSON.stringify({
      body: bodyImageUrl,
      custom: customBodyImageUrl,
      head: headImageUrl,
      transpose: transposeState.transposedBodyUrl,
      nsfw: nsfwState.nsfwImageUrl,
    });

    // Only save if state actually changed
    if (stateSignature !== lastSavedStateRef.current) {
      lastSavedStateRef.current = stateSignature;
      saveToHistory();
    }
  }, [
    bodyImageUrl,
    customBodyImageUrl,
    headImageUrl,
    transposeState.transposedBodyUrl,
    nsfwState.nsfwImageUrl,
    isGeneratingBody,
    isGeneratingHead,
    transposeState.isTransposing,
    nsfwState.isGenerating,
    saveToHistory
  ]);

  // ============================================
  // SUPABASE PERSISTENCE (via callback to parent)
  // ============================================

  // Auto-save viewport state to Supabase via callback
  const viewportSaveCallbackRef = useRef(onViewportStateChange);
  viewportSaveCallbackRef.current = onViewportStateChange;
  const viewportSaveQueueRef = useRef<ReturnType<typeof createViewportSaveQueue<ViewportState>> | null>(null);
  const hasViewportSaveCallback = !!onViewportStateChange;
  useEffect(() => {
    const queue = createViewportSaveQueue<ViewportState>(
      state => viewportSaveCallbackRef.current?.(state),
      error => console.error('Failed to persist viewport state:', error),
    );
    viewportSaveQueueRef.current = queue;
    return () => { queue.dispose(); viewportSaveQueueRef.current = null; };
  }, []);

  useEffect(() => {
    // Don't save while any generation is in progress
    if (isGeneratingBody || isGeneratingHead || transposeState.isTransposing || nsfwState.isGenerating) return;
    // Need at least one image to save
    if (!bodyImageUrl && !headImageUrl) return;
    // Need callback to save
    if (!hasViewportSaveCallback) return;

    // Convert history to persistable format (limit to last 20 entries)
    const persistableHistory: PersistableHistoryEntry[] = history.slice(-20).map(entry => ({
      bodyImageUrl: entry.bodyImageUrl,
      customBodyImageUrl: entry.customBodyImageUrl,
      headImageUrl: entry.headImageUrl,
      transposeSnapshot: entry.transposeSnapshot,
      nsfwSnapshot: entry.nsfwSnapshot,
      nsfwEnabled: entry.nsfwEnabled,
      timestamp: entry.timestamp,
    }));

    // Adjust historyIndex if we truncated history
    const adjustedHistoryIndex = history.length > 20
      ? Math.max(0, historyIndex - (history.length - 20))
      : historyIndex;

    const stateToSave: ViewportState = {
      bodyImageUrl,
      customBodyImageUrl,
      headImageUrl,
      transposeState: {
        lastTransposeTimestamp: transposeState.lastTransposeTimestamp,
        headLastModified: transposeState.headLastModified,
        bodyLastModified: transposeState.bodyLastModified,
        transposedBodyUrl: transposeState.transposedBodyUrl,
        currentTransposeVersion: transposeState.currentTransposeVersion,
      },
      nsfwState: {
        nsfwImageUrl: nsfwState.nsfwImageUrl,
        nsfwTransposeVersion: nsfwState.nsfwTransposeVersion,
      },
      nsfwEnabled,
      bodyViewTab,
      timestamp: Date.now(),
      // Include history for undo/redo persistence
      history: persistableHistory,
      historyIndex: adjustedHistoryIndex,
    };

    viewportSaveQueueRef.current?.schedule(stateToSave);
  }, [
    bodyImageUrl,
    customBodyImageUrl,
    headImageUrl,
    transposeState,
    nsfwState,
    nsfwEnabled,
    bodyViewTab,
    isGeneratingBody,
    isGeneratingHead,
    hasViewportSaveCallback,
    history,
    historyIndex,
  ]);

  // ============================================
  // LOCALSTORAGE PERSISTENCE (fast cache for reload)
  // ============================================

  // Save viewport images to localStorage for fast reload
  // This is a cache - Supabase is the source of truth
  useEffect(() => {
    // Don't save while generating
    if (isGeneratingBody || isGeneratingHead || transposeState.isTransposing || nsfwState.isGenerating) return;
    // Need at least one image to save
    if (!bodyImageUrl && !headImageUrl) return;

    const cacheData = {
      bodyImageUrl,
      customBodyImageUrl,
      headImageUrl,
      transposedBodyUrl: transposeState.transposedBodyUrl,
      nsfwImageUrl: nsfwState.nsfwImageUrl,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(VIEWPORT_STORAGE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      console.warn('Failed to save viewport to localStorage:', err);
    }
  }, [
    bodyImageUrl,
    customBodyImageUrl,
    headImageUrl,
    transposeState.transposedBodyUrl,
    nsfwState.nsfwImageUrl,
    isGeneratingBody,
    isGeneratingHead,
    transposeState.isTransposing,
    nsfwState.isGenerating,
    VIEWPORT_STORAGE_KEY,
  ]);

  // ============================================
  // EDIT HANDLERS
  // ============================================

  const handleBodyEdit = () => {
    if (!bodyEditPrompt.trim() || !bodyImageUrl) return;
    setHasUserEditedBody(true); // Mark that user has edited body - will require transpose
    setLastBodyPrompt(bodyEditPrompt); // Track for regenerate
    setLastBodyWasEdit(true);
    generateBodyImage(bodyEditPrompt, true, bodyImageUrl, bodyEditModel);
    setBodyEditPrompt('');
    setIsEditingBody(false);
  };

  const handleHeadEdit = () => {
    if (!headEditPrompt.trim() || !headImageUrl) return;
    setHasUserEditedHead(true); // Mark that user has edited head - will require transpose
    setLastHeadPrompt(headEditPrompt); // Track for regenerate
    setLastHeadWasEdit(true);
    generateHeadImage(headEditPrompt, true, headImageUrl, headEditModel);
    setHeadEditPrompt('');
    setIsEditingHead(false);
  };

  // ============================================
  // CONFIRM HANDLER
  // ============================================

  const handleConfirm = () => {
    if (!canConfirm || isAnyLoading || !bodyImageUrl || !headImageUrl) return;

    // Determine final body URL:
    // - If user edited and transposed, use transposed body
    // - If no edits, use original body (no transpose needed)
    const finalBodyUrl = hasUserEdited && transposeState.transposedBodyUrl
      ? transposeState.transposedBodyUrl
      : bodyImageUrl;

    // Include NSFW if it's from current transpose version (regardless of tab selection)
    const finalNsfwUrl = nsfwState.nsfwImageUrl &&
      nsfwState.nsfwTransposeVersion === transposeState.currentTransposeVersion
      ? nsfwState.nsfwImageUrl
      : undefined;

    // Clear localStorage cache since character is finalized
    try {
      localStorage.removeItem(VIEWPORT_STORAGE_KEY);
    } catch (err) {
      console.warn('Failed to clear viewport localStorage:', err);
    }

    onConfirm({
      bodyImageUrl,
      customBodyImageUrl: customBodyImageUrl || undefined,
      headImageUrl,
      transposedBodyUrl: finalBodyUrl, // Use appropriate body based on edit state
      nsfwImageUrl: finalNsfwUrl,
      bodyPrompt: initialBodyPrompt,
      headPrompt: initialHeadPrompt,
      sharedAttributes,
      // Pass blobs for CORS-safe finalization (bypasses fetch from R2 URLs)
      bodyBlob: bodyBlob || undefined,
      headBlob: headBlob || undefined,
      transposedBodyBlob: transposedBodyBlob || undefined,
      nsfwBlob: nsfwBlob || undefined,
    });
  };

  // ============================================
  // TRY AGAIN HANDLER
  // ============================================
  // Regenerate both head and body from scratch using initial prompts
  // Follows same flow: head first, then body with head reference

  const handleTryAgain = useCallback(async () => {
    if (isAnyLoading) return;

    // Wait for reference classification if still in progress
    if (isClassifyingReferences) {
      console.log('⏳ Waiting for reference image classification to complete...');
      return;
    }

    // Reset all image state
    setBodyImageUrl(null);
    setCustomBodyImageUrl(null);
    setHeadImageUrl(null);
    setBodyImageBase64(null);
    setHeadImageBase64(null);
    setBodyBlob(null);
    setHeadBlob(null);

    // Reset transpose state
    setTransposeState({
      lastTransposeTimestamp: null,
      headLastModified: 0,
      bodyLastModified: 0,
      transposedBodyUrl: null,
      currentTransposeVersion: 0,
      isTransposing: false
    });
    setTransposedBodyBlob(null);

    // Reset NSFW state
    setNsfwState({
      nsfwImageUrl: null,
      nsfwTransposeVersion: -1,
      isGenerating: false
    });
    setNsfwBlob(null);

    // Reset edit tracking
    setHasUserEditedHead(false);
    setHasUserEditedBody(false);

    // Reset body generation ref so it triggers again when head completes
    hasTriggeredBodyGenRef.current = false;

    // Clear history for fresh start
    setHistory([]);
    setHistoryIndex(-1);

    // Set loading state
    setIsGeneratingHead(true);
    setError(null);

    // Set model override ref for body generation (used by useEffect)
    const modelToUse: 'openai/gpt-image-2.5-sunburst' | 'prunaai/z-image-turbo' = regenModel === 'testing'
      ? 'prunaai/z-image-turbo'
      : 'openai/gpt-image-2.5-sunburst';
    regenModelOverrideRef.current = modelToUse;

    // Generate head first (body will auto-trigger when head completes via useEffect)
    console.log(`🔄 Try Again: Regenerating head and body with ${regenModel === 'testing' ? 'Testing' : 'Default'} model...`);
    if (classifiedReferenceImages.length > 0) {
      console.log(`📸 Using ${classifiedReferenceImages.length} classified reference(s) for regeneration`);
    }

    try {
      await generateHeadImage(initialHeadPrompt, false, undefined, 'default', modelToUse);
    } catch (err) {
      console.error('Try Again head generation failed:', err);
      setError('Failed to regenerate head image');
      setIsGeneratingHead(false);
    }
  }, [isAnyLoading, isClassifyingReferences, initialHeadPrompt, generateHeadImage, classifiedReferenceImages, regenModel]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="flex flex-col h-full bg-onyx text-white select-none">
      {/* Error Banner */}
      {error && (
        <div className="px-4 py-3 bg-red-500/10 border-b border-red-500/30">
          <div className="max-w-5xl mx-auto flex items-center gap-2 text-red-400">
            <WarningIcon className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Main Viewport Area */}
      <div className="flex-1 flex items-center justify-center px-8 py-2 gap-4 min-h-0">
        {/* Body Viewport - 9:16 aspect when empty, fits image when loaded */}
        <div className="flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
          {/* Header with Tabs - thin sleek frame */}
          <div className="px-2.5 py-1.5 bg-zinc-900 rounded-t-lg border-b border-graphite flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <UserIcon className="w-3.5 h-3.5 text-slate-blue" />
              <span className="text-xs font-medium text-white/90">Body</span>
              {bodyImageUrl && (
                <div className="flex bg-onyx rounded p-0.5 ml-2" role="group" aria-label="Body version">
                  <button onClick={() => setBodyViewTab('original')} aria-pressed={bodyViewTab === 'original'} className={`px-2 py-0.5 text-[10px] rounded transition-colors ${bodyViewTab === 'original' ? 'bg-graphite text-white' : 'text-gray-500 hover:text-white'}`}>Original</button>
                </div>
              )}
              {/* Optional tabs for Body, Composite, NSFW */}
              {transposeState.transposedBodyUrl && (
                <div className="flex bg-onyx rounded p-0.5 ml-2">
                  <button
                    onClick={() => setBodyViewTab('composite')}
                    className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${bodyViewTab === 'composite'
                      ? 'bg-graphite text-white'
                      : 'text-gray-500 hover:text-white'
                      }`}
                  >
                    Comp
                  </button>
                  {nsfwState.nsfwImageUrl && nsfwEnabled && (
                    <button
                      onClick={() => setBodyViewTab('nsfw')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${bodyViewTab === 'nsfw'
                        ? 'bg-[#E53E3E] text-white'
                        : 'text-rose-300 hover:text-white'
                        }`}
                    >
                      NSFW
                    </button>
                  )}
                </div>
              )}

              <div className="ml-auto flex items-center gap-1.5">
                {bodyImageUrl && (
                  <button
                    onClick={() => setFullscreenImage('body')}
                    className="p-0.5 rounded hover:bg-graphite transition-colors"
                    title="View fullscreen"
                  >
                    <ArrowsOutIcon className="w-3.5 h-3.5 text-gray-500 hover:text-white" />
                  </button>
                )}
                <span className="text-[10px] text-gray-600">{generationModel === 'openai/gpt-image-2.5-sunburst' ? '2:3' : '9:16'}</span>
              </div>
            </div>
          </div>

          {/* Image Container - 9:16 aspect when empty, contains image when loaded */}
          <div className={`overflow-hidden min-h-0 bg-neutral-300 flex items-center justify-center ${!bodyImageUrl ? (generationModel === 'openai/gpt-image-2.5-sunburst' ? 'h-full aspect-[2/3]' : 'h-full aspect-[9/16]') : 'flex-1'}`}>
            {/* Show spinner only during initial generation (no image yet) */}
            {isGeneratingBody && !bodyImageUrl ? (
              <div className="flex flex-col items-center gap-3">
                <CircleNotchIcon className="w-8 h-8 text-slate-blue animate-spin" />
                <span className="text-sm text-gray-600">Generating body...</span>
              </div>
            ) : bodyViewTab === 'custom' && customBodyImageUrl ? (
              <img src={customBodyImageUrl} alt="Body Unclothed" onLoad={() => setBodyImageLoaded(true)} className="max-h-full max-w-full object-contain" />
            ) : bodyViewTab === 'nsfw' && nsfwState.nsfwImageUrl ? (
              <img
                src={nsfwState.nsfwImageUrl}
                alt="Body NSFW"
                onLoad={() => setBodyImageLoaded(true)}
                className={`max-h-full max-w-full object-contain transition-opacity duration-700 ease-in-out ${bodyImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              />
            ) : bodyViewTab === 'composite' && transposeState.transposedBodyUrl ? (
              <img
                src={transposeState.transposedBodyUrl}
                alt="Body Composite"
                onLoad={() => setBodyImageLoaded(true)}
                className={`max-h-full max-w-full object-contain transition-opacity duration-700 ease-in-out ${bodyImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              />
            ) : bodyImageUrl ? (
              <img
                src={bodyImageUrl}
                alt="Body"
                onLoad={() => setBodyImageLoaded(true)}
                className={`max-h-full max-w-full object-contain transition-opacity duration-700 ease-in-out ${bodyImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              />
            ) : (
              <div className="text-sm text-gray-600">Body preview</div>
            )}
          </div>

          {/* Edit Panel - thin sleek footer */}
          <div className="px-2.5 py-2 bg-zinc-900 rounded-b-lg border-t border-graphite flex-shrink-0">
            {isEditingBody ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={bodyEditPrompt}
                  onChange={(e) => setBodyEditPrompt(e.target.value)}
                  placeholder="Describe changes..."
                  className="w-full px-2.5 py-1.5 bg-onyx rounded text-xs text-white placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-slate-blue"
                  onKeyDown={(e) => e.key === 'Enter' && handleBodyEdit()}
                />
                {/* Model Selection Toggle */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500">Model</span>
                  <div className="flex bg-onyx rounded p-0.5">
                    <button
                      onClick={() => setBodyEditModel('default')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${bodyEditModel === 'default'
                        ? 'bg-graphite text-white'
                        : 'text-gray-500 hover:text-white'
                        }`}
                    >
                      Default
                    </button>
                    <button
                      onClick={() => setBodyEditModel('testing')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${bodyEditModel === 'testing'
                        ? 'bg-graphite text-white'
                        : 'text-gray-500 hover:text-white'
                        }`}
                    >
                      Testing
                    </button>
                  </div>
                </div>
                {/* Strength slider only for testing model (z-image-turbo) */}
                {bodyEditModel === 'testing' && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-500">Strength</span>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      value={bodyStrength}
                      onChange={(e) => setBodyStrength(parseFloat(e.target.value))}
                      className="flex-1 h-0.5 bg-iron rounded-full appearance-none cursor-pointer"
                    />
                    <span className="text-[10px] text-gray-400 w-6">{bodyStrength.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setIsEditingBody(false)}
                    className="flex-1 py-1 text-[10px] text-gray-500 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBodyEdit}
                    disabled={!bodyEditPrompt.trim() || isGeneratingBody}
                    className="flex-1 py-1 bg-slate-blue rounded text-[10px] text-white disabled:opacity-50"
                  >
                    Apply
                  </button>
                </div>
              </div>
            ) : isGeneratingBody ? (
              /* Show loading state - different text for initial generation vs edit */
              <div className="w-full flex items-center justify-center gap-1.5 py-1 text-xs text-slate-blue">
                <CircleNotchIcon className="w-3.5 h-3.5 animate-spin" />
                <span>{bodyImageUrl ? 'Applying edit...' : 'Generating body...'}</span>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    // Always edit the original body, switch to original tab
                    setBodyViewTab('original');
                    setIsEditingBody(true);
                  }}
                  disabled={!bodyImageUrl || isGeneratingBody}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1 text-xs text-gray-500 hover:text-white disabled:opacity-50 transition-colors"
                >
                  <PencilSimpleIcon className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => {
                    setHasUserEditedBody(true);
                    if (lastBodyWasEdit && bodyImageUrl) {
                      // Regenerate with last edit prompt (as edit)
                      generateBodyImage(lastBodyPrompt, true, bodyImageUrl, bodyEditModel);
                    } else {
                      // Regenerate with initial prompt (fresh, with head reference)
                      // Pass headImageBase64 to bypass CORS when fetching head from R2
                      generateBodyImage(initialBodyPrompt, false, undefined, 'default', headImageUrl || undefined, headImageBase64 || undefined);
                    }
                  }}
                  disabled={!bodyImageUrl || isGeneratingBody}
                  title={lastBodyWasEdit ? `Regenerate: "${lastBodyPrompt.slice(0, 30)}..."` : "Regenerate with initial prompt"}
                  className="flex items-center justify-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-white disabled:opacity-50 transition-colors hover:bg-graphite rounded"
                >
                  <ArrowsClockwiseIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Transpose Button */}
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleTranspose}
            disabled={!bodyImageUrl || !headImageUrl || transposeState.isTransposing || isGeneratingBody || isGeneratingHead || !needsTranspose}
            className={`
              relative p-4 rounded-full transition-all
              ${needsTranspose && bodyImageUrl && headImageUrl
                ? 'bg-slate-blue shadow-lg shadow-slate-blue/50 animate-pulse hover:shadow-slate-blue/70'
                : transposeState.transposedBodyUrl
                  ? 'bg-graphite text-gray-600'
                  : 'bg-graphite text-gray-500 hover:bg-iron'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {transposeState.isTransposing ? (
              <CircleNotchIcon className="w-6 h-6 animate-spin text-white" />
            ) : (
              <ArrowLeftIcon className={`w-6 h-6 ${needsTranspose ? 'text-white' : ''}`} />
            )}
          </button>
          <span className="text-xs text-gray-500 text-center max-w-[80px]">
            {transposeState.isTransposing
              ? 'Compositing...'
              : needsTranspose
                ? 'Transpose head'
                : transposeState.transposedBodyUrl
                  ? 'Up to date'
                  : 'Transpose'
            }
          </span>
        </div>

        {/* Head Viewport - 3:4 aspect when empty, fits image when loaded */}
        <div className="flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
          {/* Header - thin sleek frame */}
          <div className="px-2.5 py-1.5 bg-zinc-900 rounded-t-lg border-b border-graphite flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <SmileyIcon className="w-3.5 h-3.5 text-slate-blue" />
              <span className="text-xs font-medium text-white/90">Head</span>
              <div className="ml-auto flex items-center gap-1.5">
                {/* Fullscreen button */}
                {headImageUrl && (
                  <button
                    onClick={() => setFullscreenImage('head')}
                    className="p-0.5 rounded hover:bg-graphite transition-colors"
                    title="View fullscreen"
                  >
                    <ArrowsOutIcon className="w-3.5 h-3.5 text-gray-500 hover:text-white" />
                  </button>
                )}
                <span className="text-[10px] text-gray-600">3:4</span>
              </div>
            </div>
          </div>

          {/* Image Container - 3:4 aspect when empty, contains image when loaded */}
          <div className={`overflow-hidden min-h-0 bg-neutral-300 flex items-center justify-center ${!headImageUrl ? 'h-full aspect-[3/4]' : 'flex-1'}`}>
            {/* Show spinner only during initial generation (no image yet) */}
            {isGeneratingHead && !headImageUrl ? (
              <div className="flex flex-col items-center gap-3">
                <CircleNotchIcon className="w-8 h-8 text-slate-blue animate-spin" />
                <span className="text-sm text-gray-600">Generating head...</span>
              </div>
            ) : headImageUrl ? (
              <img
                src={headImageUrl}
                alt="Head"
                onLoad={() => setHeadImageLoaded(true)}
                className={`max-h-full max-w-full object-contain transition-opacity duration-700 ease-in-out ${headImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              />
            ) : (
              <div className="text-sm text-gray-600">Head preview</div>
            )}
          </div>

          {/* Edit Panel - thin sleek footer */}
          <div className="px-2.5 py-2 bg-zinc-900 rounded-b-lg border-t border-graphite flex-shrink-0">
            {isEditingHead ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={headEditPrompt}
                  onChange={(e) => setHeadEditPrompt(e.target.value)}
                  placeholder="Describe changes..."
                  className="w-full px-2.5 py-1.5 bg-onyx rounded text-xs text-white placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-slate-blue"
                  onKeyDown={(e) => e.key === 'Enter' && handleHeadEdit()}
                />
                {/* Model Selection Toggle */}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500">Model</span>
                  <div className="flex bg-onyx rounded p-0.5">
                    <button
                      onClick={() => setHeadEditModel('default')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${headEditModel === 'default'
                        ? 'bg-graphite text-white'
                        : 'text-gray-500 hover:text-white'
                        }`}
                    >
                      Default
                    </button>
                    <button
                      onClick={() => setHeadEditModel('testing')}
                      className={`px-1.5 py-0.5 text-[10px] rounded transition-colors ${headEditModel === 'testing'
                        ? 'bg-graphite text-white'
                        : 'text-gray-500 hover:text-white'
                        }`}
                    >
                      Testing
                    </button>
                  </div>
                </div>
                {/* Strength slider only for testing model (z-image-turbo) */}
                {headEditModel === 'testing' && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-500">Strength</span>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      value={headStrength}
                      onChange={(e) => setHeadStrength(parseFloat(e.target.value))}
                      className="flex-1 h-0.5 bg-iron rounded-full appearance-none cursor-pointer"
                    />
                    <span className="text-[10px] text-gray-400 w-6">{headStrength.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setIsEditingHead(false)}
                    className="flex-1 py-1 text-[10px] text-gray-500 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleHeadEdit}
                    disabled={!headEditPrompt.trim() || isGeneratingHead}
                    className="flex-1 py-1 bg-slate-blue rounded text-[10px] text-white disabled:opacity-50"
                  >
                    Apply
                  </button>
                </div>
              </div>
            ) : isGeneratingHead ? (
              /* Show loading state - different text for initial generation vs edit */
              <div className="w-full flex items-center justify-center gap-1.5 py-1 text-xs text-slate-blue">
                <CircleNotchIcon className="w-3.5 h-3.5 animate-spin" />
                <span>{headImageUrl ? 'Applying edit...' : 'Generating head...'}</span>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <button
                  onClick={() => setIsEditingHead(true)}
                  disabled={!headImageUrl || isGeneratingHead}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1 text-xs text-gray-500 hover:text-white disabled:opacity-50 transition-colors"
                >
                  <PencilSimpleIcon className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => {
                    setHasUserEditedHead(true);
                    if (lastHeadWasEdit && headImageUrl) {
                      // Regenerate with last edit prompt (as edit)
                      generateHeadImage(lastHeadPrompt, true, headImageUrl, headEditModel);
                    } else {
                      // Regenerate with initial prompt (fresh)
                      generateHeadImage(initialHeadPrompt, false);
                    }
                  }}
                  disabled={!headImageUrl || isGeneratingHead}
                  title={lastHeadWasEdit ? `Regenerate: "${lastHeadPrompt.slice(0, 30)}..."` : "Regenerate with initial prompt"}
                  className="flex items-center justify-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-white disabled:opacity-50 transition-colors hover:bg-graphite rounded"
                >
                  <ArrowsClockwiseIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Back to Builder Confirmation Modal */}
      {showBackConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-carbon border border-iron rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center">
                <WarningIcon className="w-7 h-7 text-amber-400" weight="fill" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Go Back to Builder?</h3>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-6">
              Going back will discard your generated images and prompts. You&apos;ll start fresh from the character description.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowBackConfirmation(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-iron text-neutral-200 hover:bg-graphite transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowBackConfirmation(false);

                  // Delete temp images from R2
                  const urlsToDelete = [
                    bodyImageUrl,
                    headImageUrl,
                    transposeState.transposedBodyUrl,
                    nsfwState.nsfwImageUrl
                  ].filter((url): url is string => !!url);

                  for (const url of urlsToDelete) {
                    try {
                      await deleteFromR2(url);
                      console.log('🗑️ Deleted temp image:', url.substring(url.lastIndexOf('/') + 1));
                    } catch (error) {
                      console.warn('⚠️ Failed to delete temp image:', error);
                    }
                  }

                  // Clear localStorage cache
                  try {
                    localStorage.removeItem(VIEWPORT_STORAGE_KEY);
                    console.log('🗑️ Cleared viewport localStorage cache');
                  } catch (err) {
                    console.warn('Failed to clear viewport localStorage:', err);
                  }

                  // Parent will handle clearing builder_session in Supabase
                  onBack();
                }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-400 hover:bg-amber-500/30 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="px-4 py-4 bg-gradient-to-t from-carbon to-transparent border-t border-iron/30">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          {/* Navigation Buttons */}
          <div className="flex items-center gap-2">
            {onReturnToCharacters && (
              <button
                onClick={onReturnToCharacters}
                disabled={isAnyLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-gray-500 hover:text-white hover:bg-graphite transition-all text-sm disabled:opacity-50"
              >
                <UsersIcon className="w-4 h-4" />
                <span>Return to Characters</span>
              </button>
            )}
            <button
              onClick={() => setShowBackConfirmation(true)}
              disabled={isAnyLoading}
              className="px-4 py-2.5 rounded-xl text-gray-500 hover:text-white hover:bg-graphite transition-all text-sm disabled:opacity-50"
            >
              Back to Builder
            </button>
          </div>

          <div className="flex-1" />

          {/* Undo/Redo Buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleUndo}
              disabled={!canUndo || isAnyLoading}
              title="Undo"
              className={`
                p-2.5 rounded-lg transition-all
                ${canUndo && !isAnyLoading
                  ? 'bg-graphite text-gray-400 hover:text-white hover:bg-iron'
                  : 'bg-carbon text-gray-600 cursor-not-allowed'
                }
              `}
            >
              <ArrowUUpLeftIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo || isAnyLoading}
              title="Redo"
              className={`
                p-2.5 rounded-lg transition-all
                ${canRedo && !isAnyLoading
                  ? 'bg-graphite text-gray-400 hover:text-white hover:bg-iron'
                  : 'bg-carbon text-gray-600 cursor-not-allowed'
                }
              `}
            >
              <ArrowUUpRightIcon className="w-4 h-4" />
            </button>
            {/* History indicator */}
            {history.length > 1 && (
              <span className="text-xs text-gray-600 ml-1">
                {historyIndex + 1}/{history.length}
              </span>
            )}
          </div>

          {/* NSFW Generate Button - DISABLED FOR NOW */}
          {/* TODO: Re-enable when NSFW generation is working properly
          <button
            onClick={() => {
              if (nsfwState.nsfwImageUrl) {
                setBodyViewTab('nsfw');
              } else {
                setNsfwEnabled(true);
              }
            }}
            disabled={!canConfirm || nsfwState.isGenerating}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-all
              ${!canConfirm
                ? 'bg-carbon text-gray-600 cursor-not-allowed'
                : nsfwState.nsfwImageUrl
                  ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                  : 'bg-graphite text-gray-500 hover:text-white'
              }
              disabled:opacity-50
            `}
          >
            {nsfwState.isGenerating ? (
              <CircleNotchIcon className="w-4 h-4 animate-spin" />
            ) : nsfwState.nsfwImageUrl ? (
              <EyeSlashIcon className="w-4 h-4" />
            ) : (
              <EyeIcon className="w-4 h-4" />
            )}
            <span>{nsfwState.nsfwImageUrl ? 'View NSFW' : 'Generate NSFW'}</span>
          </button>
          */}

          {/* Try Again with Model Toggle */}
          <div className="flex items-center gap-2">
            {/* Model Toggle */}
            <div className="flex bg-carbon rounded-lg p-0.5 border border-iron">
              <button
                onClick={() => setRegenModel('default')}
                disabled={isAnyLoading}
                className={`px-2.5 py-1.5 text-xs rounded-md transition-colors ${regenModel === 'default'
                  ? 'bg-graphite text-white'
                  : 'text-gray-500 hover:text-white'
                  } disabled:opacity-50`}
                title="Use Default model (Sunburst)"
              >
                Default
              </button>
              <button
                onClick={() => setRegenModel('testing')}
                disabled={isAnyLoading}
                className={`px-2.5 py-1.5 text-xs rounded-md transition-colors ${regenModel === 'testing'
                  ? 'bg-graphite text-white'
                  : 'text-gray-500 hover:text-white'
                  } disabled:opacity-50`}
                title="Use Testing model (z-image-turbo)"
              >
                Testing
              </button>
            </div>

            {/* Try Again Button */}
            <button
              onClick={handleTryAgain}
              disabled={isAnyLoading || isClassifyingReferences}
              className={`
                flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all
                ${!isAnyLoading && !isClassifyingReferences
                  ? 'bg-graphite text-white border border-iron hover:bg-iron active:scale-95'
                  : 'bg-carbon text-gray-600 cursor-not-allowed'
                }
              `}
              title={isClassifyingReferences
                ? 'Waiting for reference images to be processed...'
                : `Regenerate with ${regenModel === 'testing' ? 'Testing' : 'Default'} model${classifiedReferenceImages.length > 0 ? ` (${classifiedReferenceImages.length} ref images)` : ''}`
              }
            >
              <ArrowsClockwiseIcon className="w-4 h-4" />
              <span>{isClassifyingReferences ? 'Processing...' : 'Try Again'}</span>
            </button>

          </div>

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isAnyLoading}
            className={`
              flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-medium transition-all
              ${canConfirm && !isAnyLoading
                ? 'bg-gradient-to-r from-slate-blue to-[#9795FA] text-white shadow-lg shadow-slate-blue/30 hover:shadow-slate-blue/50 active:scale-95'
                : 'bg-carbon text-gray-600 cursor-not-allowed'
              }
            `}
          >
            {isAnyLoading ? (
              <>
                <CircleNotchIcon className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : hasUserEdited && !transposeState.transposedBodyUrl ? (
              <>
                <span>Transpose First</span>
              </>
            ) : hasUserEdited && needsTranspose ? (
              <>
                <span>Re-transpose</span>
              </>
            ) : (
              <>
                <span>Confirm</span>
                <CaretRightIcon className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>


      {/* Fullscreen Image Viewer */}
      {fullscreenImage && (
        <FullscreenImageViewer
          images={
            fullscreenImage === 'body'
              ? [
                // Show the currently viewed body image
                bodyViewTab === 'custom' && customBodyImageUrl
                  ? { url: customBodyImageUrl, label: 'Body (Unclothed)' }
                  : bodyViewTab === 'nsfw' && nsfwState.nsfwImageUrl
                  ? { url: nsfwState.nsfwImageUrl, label: 'Body (NSFW)' }
                  : bodyViewTab === 'composite' && transposeState.transposedBodyUrl
                    ? { url: transposeState.transposedBodyUrl, label: 'Body (Composite)' }
                    : bodyImageUrl
                      ? { url: bodyImageUrl, label: 'Body (Original)' }
                      : []
              ].flat().filter((img): img is ViewerImage => !!img && !!img.url)
              : headImageUrl
                ? [{ url: headImageUrl, label: 'Head' }]
                : []
          }
          currentIndex={0}
          onClose={() => setFullscreenImage(null)}
          fixed={true}
          respectSidebars={true}
        />
      )}
    </div>
  );
}
